import { saveAs } from 'file-saver';
import { get, groupBy, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ActionType } from 'react-table';

import { SEARCH_RESULTS_LIMIT } from '~/common/constants/max_lengths';
import { NAV_AND_SUBNAV_HEIGHT } from '~/common/constants/page';
import { getSearchConstants } from '~/common/constants/search';
import { canDownloadSearchResultItem } from '~/components/AnalyzerResultsPage/SearchResultsItem/utils';
import { getHandlersKeyFromSearchResult } from '~/components/SearchV2/SearchResult/SearchResult.utils';
import { trackSegment } from '~/components/SegmentAnalytics';
import { DocumentDetailsPanel } from '~/components/Shared/DocumentDetailsPanel/DocumentDetailsPanel';
import { useDocumentMultipleLocationsModal } from '~/components/Shared/EcModal/useDocumentMultipleLocationsModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { ExportExcel } from '~/components/Shared/ExportExcel/ExportExcel';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { useTableSelection } from '~/eds/src/hooks/useTableSelection';
import { Table } from '~/eds/src/modules/Table/Table';
import { Action } from '~/eds/src/types/Action';
import { TableState } from '~/eds/src/types/Table';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import { ClauseValue } from '~/evifields/src/types/Value';
import {
  SearchDocumentItem,
  SearchDocumentsResult,
  SearchQuery,
} from '~/features/advanced-search/types';
import { testIsAnyDocumentNotMovable } from '~/features/search/utils';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientSubDomain } from '~/hooks/useClientSubDomain';
import { usePermission } from '~/hooks/usePermission';
import { useSearchColumns } from '~/hooks/useSearchColumns';
import { useSearchNavigation } from '~/hooks/useSearchNavigation';
import { api } from '~/redux/api/createApi';
import { Dashboard } from '~/redux/api/methods/dashboardV2';
import { TableViewSet } from '~/redux/api/methods/tableViews';
import { slices } from '~/redux/slices';
import { SearchFilter } from '~/types/DashboardV2';
import { ERROR, WARNING } from '~/types/toast.types';
import { openInNewTab } from '~/utils/browser';
import { testContainsGhostSearchResult } from '~/utils/document.utils';
import { getMaximumByLimit } from '~/utils/number';
import { HandlerName } from '~/utils/table';

import { getPath } from './DashboardV2SearchResults.utils';
import { DetailsPanel } from './DetailsPanel/DetailsPanel';

type Props = {
  currentTableView?: TableViewSet;
  dashboard?: Dashboard;
  isLoading: boolean;
  data?: SearchDocumentsResult;
  query: SearchQuery;
  pageSize?: number;
  searchFilters: Record<FieldId, SearchFilter>;
  tableSelection: ReturnType<typeof useTableSelection>;
  resultsCache?: Record<string, SearchDocumentItem>;
  refreshSearch: () => void;
  onMove: () => void;
  onCopy: () => void;
  onEdit: () => void;
  onEditClauses: () => void;
  onDelete: () => void;
  onAddToGroup: () => void;
  onLoadView: (tableState: TableState) => void;
  onSaveView: (tableState: TableState) => void;
};

const DashboardV2SearchResults = ({
  currentTableView,
  dashboard,
  isLoading,
  data,
  pageSize,
  query,
  searchFilters,
  tableSelection,
  resultsCache = {},
  refreshSearch,
  onMove,
  onCopy,
  onEdit,
  onEditClauses,
  onDelete,
  onAddToGroup,
  onLoadView,
  onSaveView,
}: Props) => {
  const intl = useIntl();
  const page = useSelector(slices.dashboardV2.selectors.selectPage);
  const sortBy = useSelector(slices.dashboardV2.selectors.selectSortBy);
  const columnOrder = useSelector(
    slices.dashboardV2.selectors.selectColumnOrder,
  );
  const columnWidths = useSelector(
    slices.dashboardV2.selectors.selectColumnWidths,
  );
  const activeFilters = useSelector(
    slices.dashboardV2.selectors.selectActiveFilters,
  );
  const featuredClauses = useMemo(() => {
    return (
      activeFilters.filter(
        (filter) =>
          filter.fieldId === 'clause' && filter.operatorId === 'contains_any',
      ) as Filter<ClauseValue>[]
    ).map((clauseFilter) => clauseFilter.values[0]?.provision ?? '');
  }, [activeFilters]);
  const { EXPORT_DUPLICATED_TEXT, EXPORT_COMPLETED_TEXT, EXPORT_DEFAULT_TEXT } =
    getSearchConstants(intl);

  const dispatch = useDispatch();
  const client = useClientSubDomain();
  const isUnifiedDetailsPanelEnabled = useFlag(
    FlagType.UnifiedDocumentDetailsPanel,
  );
  const [documentDetails, setDocumentDetails] = useState<SearchDocumentItem>();
  const [showExportExcel, setShowExportExcel] = useState(false);
  const [getDocumentOriginal] =
    api.endpoints.getDocumentOriginal.useLazyQuery();

  const hasExportExcel = useFlag(FlagType.DashboardsExportExcel);
  const hasGroupsEditPermission = usePermission({
    permission: {
      resourceId: 'document_groups',
      resourceType: 'edit',
    },
  });

  const hasDocumentPermission = usePermission({
    permission: {
      resourceId: 'documents',
      resourceType: 'edit',
    },
  });

  const isBulkActionOnDashboardEnabled = useFlag(
    FlagType.DashboardV2DocumentsBulkActions,
  );

  const showDetailsPanel = (rowData: any) => {
    setDocumentDetails(rowData);
    trackSegment('selectOverlayPanel', {
      name: rowData.id,
    });
  };
  const hideDetailsPanel = () => {
    trackSegment('selectClose', {
      name: documentDetails?.id,
    });
    setDocumentDetails(undefined);
  };

  const saveOriginalDocument = async (document: SearchDocumentItem) => {
    const documentHandler = get(document, 'document_handlers[0]', {});
    const { id, document_name, file_type } = documentHandler;
    if (canDownloadSearchResultItem(document)) {
      try {
        const response = await getDocumentOriginal(id).unwrap();
        saveAs(response!, `${document_name}.${file_type}`);
      } catch {
        showToast(
          ERROR,
          intl.formatMessage(
            {
              id: 'DashboardV2SearchResults.downloadError',
              defaultMessage:
                'Something went wrong with downloading {documentName}',
              description:
                'Error message when document download fails. Example: documentName = "contract.pdf"',
            },
            { documentName: document_name },
          ),
        );
      }
    } else {
      showToast(
        WARNING,
        intl.formatMessage(
          {
            id: 'DashboardV2SearchResults.downloadPermissionError',
            defaultMessage:
              "You don't have permissions to download {documentName}",
            description:
              'Warning when user lacks download permissions. Example: documentName = "contract.pdf"',
          },
          { documentName: document_name },
        ),
      );
    }
  };

  const isDuplicateExport = data?.meta.is_duplicate_export;
  const exportButtonTooltip = isDuplicateExport
    ? `${EXPORT_DUPLICATED_TEXT}  ${EXPORT_COMPLETED_TEXT}`
    : EXPORT_DEFAULT_TEXT;

  const actions = hasExportExcel
    ? [
        {
          icon: 'file-spreadsheet',
          label: EXPORT_DEFAULT_TEXT,
          tooltip: exportButtonTooltip,
          disabled: isDuplicateExport,
          onClick: () => {
            setShowExportExcel(true);
          },
        },
      ]
    : [];

  const onOpenDocumentClick = () => {
    if (documentDetails) {
      if (documentDetails.document_handlers.length > 1) {
        //@ts-ignore callback type not supported in js hook
        setDocumentSelected(documentDetails);
        //@ts-ignore callback type not supported in js hook
        setOpenDocumentInNewTab(true);
      } else {
        openInNewTab(
          `/${client}/document/${documentDetails.document_handlers[0]?.id}`,
        );
      }
      trackSegment('selectDocument', {
        name: documentDetails.id,
      });
    }
  };

  const currentSelectedIndex = useMemo(() => {
    if (documentDetails) {
      return data?.results.indexOf(documentDetails);
    }
    return undefined;
  }, [data, documentDetails]);

  const prev = () => {
    currentSelectedIndex !== undefined &&
      setDocumentDetails(data?.results[currentSelectedIndex - 1]);
  };

  const next = () => {
    currentSelectedIndex !== undefined &&
      setDocumentDetails(data?.results[currentSelectedIndex + 1]);
  };

  const getPanelActions = (
    list: Array<SearchDocumentItem>,
    item: SearchDocumentItem,
  ) => {
    const isFirst = currentSelectedIndex === 0;
    const isLast = currentSelectedIndex === list.length - 1;

    const actions: Action[] = [
      {
        icon: 'open-in-new',
        label: intl.formatMessage({
          id: 'DashboardV2SearchResults.openDocumentAction',
          defaultMessage: 'Open Document',
          description: 'Action label to open document',
        }),
        value: null,
        onClick: onOpenDocumentClick,
      },
      {
        icon: 'download',
        label: intl.formatMessage({
          id: 'DashboardV2SearchResults.downloadAction',
          defaultMessage: 'Download',
          description: 'Action label to download document',
        }),
        value: null,
        onClick: () => {
          saveOriginalDocument(item);
          trackSegment('selectDownloadDocument', {
            name: item.id,
          });
        },
      },
      {
        disabled: isFirst,
        value: null,
        icon: 'chevron-left',
        label: intl.formatMessage({
          id: 'DashboardV2SearchResults.previousAction',
          defaultMessage: 'Previous',
          description: 'Action label for previous navigation',
        }),
        onClick: () => {
          prev();
          trackSegment('selectPreviousDocument', {
            name: item.id,
          });
        },
      },
      {
        disabled: isLast,
        icon: 'chevron-right',
        label: intl.formatMessage({
          id: 'DashboardV2SearchResults.nextAction',
          defaultMessage: 'Next',
          description: 'Action label for next navigation',
        }),
        value: null,
        onClick: () => {
          next();
          trackSegment('selectNextDocument', {
            name: item.id,
          });
        },
      },
    ];
    return actions;
  };

  const testIsAnyNotMovable = (selectedRowIds: any[]) => {
    const selectedRows = selectedRowIds.map((id) => resultsCache[id]);

    return testIsAnyDocumentNotMovable(selectedRows);
  };

  const testContainsGhost = (selectedRowIds: string[]) => {
    const selectedRows = selectedRowIds.map((id) => resultsCache[id]);

    return testContainsGhostSearchResult(selectedRows);
  };

  const getBulkActions = ({ selectedRowIds }: TableState) => {
    if (!isBulkActionOnDashboardEnabled) return [];

    const isAnyNotMovable = testIsAnyNotMovable(selectedRowIds);
    const containsGhost = testContainsGhost(selectedIds);

    const bulkActions = [
      {
        icon: 'folder',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DashboardV2SearchResults.movePrivilegesError',
              defaultMessage:
                'You do not have privileges to move one or more of the items you have selected.',
              description: 'Error message when user lacks move privileges',
            })
          : intl.formatMessage({
              id: 'DashboardV2SearchResults.moveAction',
              defaultMessage: 'Move',
              description: 'Bulk action label to move documents',
            }),
        onClick: onMove,
        disabled: isAnyNotMovable || !hasDocumentPermission.hasPermission,
      },
      {
        icon: 'clipboard',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DashboardV2SearchResults.copyPrivilegesError',
              defaultMessage:
                'You do not have privileges to copy one or more of the items you have selected.',
              description: 'Error message when user lacks copy privileges',
            })
          : intl.formatMessage({
              id: 'DashboardV2SearchResults.copyAction',
              defaultMessage: 'Copy',
              description: 'Bulk action label to copy documents',
            }),
        onClick: onCopy,
        disabled: isAnyNotMovable || !hasDocumentPermission.hasPermission,
      },
      {
        icon: 'edit',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DashboardV2SearchResults.editPrivilegesError',
              defaultMessage:
                'You do not have privileges to edit one or more of the items you have selected.',
              description: 'Error message when user lacks edit privileges',
            })
          : intl.formatMessage({
              id: 'DashboardV2SearchResults.editFieldsAction',
              defaultMessage: 'Edit Fields',
              description: 'Bulk action label to edit fields',
            }),
        onClick: onEdit,
        disabled: isAnyNotMovable || !hasDocumentPermission.hasPermission,
      },
      {
        icon: 'edit',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DashboardV2SearchResults.editClausesPrivilegesError',
              defaultMessage:
                'You do not have privileges to edit one or more of the items you have selected.',
              description:
                'Error message when user lacks edit clauses privileges',
            })
          : intl.formatMessage({
              id: 'DashboardV2SearchResults.editClausesAction',
              defaultMessage: 'Edit Clauses',
              description: 'Bulk action label to edit clauses',
            }),
        onClick: onEditClauses,
        disabled: isAnyNotMovable || !hasDocumentPermission.hasPermission,
      },
      {
        icon: 'trash',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DashboardV2SearchResults.deletePrivilegesError',
              defaultMessage:
                'You do not have privileges to delete one or more of the items you have selected.',
              description: 'Error message when user lacks delete privileges',
            })
          : intl.formatMessage({
              id: 'DashboardV2SearchResults.deleteAction',
              defaultMessage: 'Delete',
              description: 'Bulk action label to delete documents',
            }),
        onClick: onDelete,
        disabled: isAnyNotMovable || !hasDocumentPermission.hasPermission,
      },
      {
        icon: 'groups',
        label: containsGhost
          ? intl.formatMessage({
              id: 'DashboardV2SearchResults.addToGroupPrivilegesError',
              defaultMessage:
                'You do not have privileges to add one or more of the items you have selected to a group.',
              description:
                'Error message when user lacks add to group privileges',
            })
          : intl.formatMessage({
              id: 'DashboardV2SearchResults.addToGroupAction',
              defaultMessage: 'Add to Group',
              description: 'Bulk action label to add to group',
            }),
        onClick: onAddToGroup,
        disabled: containsGhost || !hasGroupsEditPermission.hasPermission,
      },
    ];

    return bulkActions;
  };

  /*
   * Navigation
   */

  const [handleNavigate] = useSearchNavigation({
    sortBy,
    query,
    pathname: getPath(),
    name: 'dashboard results',
  });
  const [
    setDocumentSelected,
    renderMultipleLocationModal,
    setOpenDocumentInNewTab,
  ] = useDocumentMultipleLocationsModal(
    getHandlersKeyFromSearchResult,
    false,
    handleNavigate,
  );
  /*
   * Navigation End
   */

  const onSetColumnOrder = useCallback(
    (updatedColumnOrder: Array<string>) => {
      dispatch(slices.dashboardV2.actions.setColumnOrder(updatedColumnOrder));
      dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
    },
    [searchFilters],
  );

  const onPaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      if (pageIndex !== page) {
        dispatch(slices.dashboardV2.actions.setPage(pageIndex));
      }
    },
    [page],
  );

  const onPageSizeChange = useCallback((pageSize: number) => {
    dispatch(slices.dashboardV2.actions.setPageSize(pageSize));
  }, []);

  // Using type any since there no interface for inner table state.
  const handleUpdate = (state: any, action?: ActionType) => {
    onTableUpdate(state, action);
    if (action?.type === 'columnDoneResizing') {
      dispatch(
        slices.dashboardV2.actions.patchColumnWidths(
          state.columnResizing.columnWidths,
        ),
      );
    }
    if (action?.type === 'toggleSortBy') {
      dispatch(slices.dashboardV2.actions.setSortBy(state.sortBy[0]));
    }
  };

  /*
   * Columns
   */

  const hardcodedColumns = [
    {
      key: 'name',
      title: intl.formatMessage({
        id: 'DashboardV2SearchResults.nameColumnTitle',
        defaultMessage: 'Name',
        description: 'Column title for document name',
      }),
      minWidth: 'm',
      renderCell: (document: SearchDocumentItem) => {
        const handlers = document.document_handlers;
        return (
          <HandlerName
            onNavigate={handleNavigate}
            handlers={handlers as any}
            onClick={() => {
              //@ts-ignore callback type not supported in js hook
              setDocumentSelected(document);
              //@ts-ignore callback type not supported in js hook
              setOpenDocumentInNewTab(false);
            }}
          />
        );
      },
    },
  ];

  const searchFilterColumns = useMemo(
    () =>
      Object.values(searchFilters).filter(
        (searchFilters) => searchFilters.is_requestable_column,
      ),
    [searchFilters],
  );

  const columns = useSearchColumns({
    columnWidths,
    hardcodedColumns,
    searchFilterColumns,
  });

  const sectionsResult = api.endpoints.getFilterSections.useQuery(undefined);

  const { data: filterSections, isSuccess: isSuccessFilterSections } =
    sectionsResult;

  const columnsMap = groupBy(columns, 'section');

  const columnGroups = useMemo(() => {
    if (filterSections?.fieldGroups) {
      return filterSections?.fieldGroups
        .map((group) => ({
          name: group.id,
          label: group.label,
          columns: columnsMap[group.id]?.map((c) => c.key),
        }))
        .filter((group) => group.columns?.length);
    }
    return undefined;
  }, [columnsMap, filterSections]);

  useEffect(() => {
    if (isSuccessFilterSections && currentTableView) {
      const hardcodedColumnKeys = hardcodedColumns.map((col) => col.key);
      const newColumnOrderFromBE = currentTableView.map((col) =>
        String(col.field_id),
      );
      onSetColumnOrder(uniq([...hardcodedColumnKeys, ...newColumnOrderFromBE]));
    }
  }, [currentTableView, isSuccessFilterSections]);

  /*
   * Columns End
   */

  const { selectedIds, isAllSelected, onTableUpdate } = tableSelection;

  const TableComponent = dashboard?.is_default ? Table : PersistedTable;
  const placeholderContent =
    data && !data.results.length
      ? {
          title: intl.formatMessage({
            id: 'DashboardV2SearchResults.noDocumentsTitle',
            defaultMessage: 'No documents found',
            description: 'Title when no documents are found',
          }),
        }
      : undefined;

  const selectedFieldIds = columnOrder
    .filter((col) => col !== 'name')
    .map(String);
  return (
    <ContentContainer placeholderContent={placeholderContent}>
      <Layout direction="column" spacing={8}>
        <>
          <TableComponent
            context={`DASHBOARD:${dashboard?.id}`}
            name="search result"
            activeRowId={documentDetails?.id}
            totalCount={getMaximumByLimit(
              data?.meta?.total,
              SEARCH_RESULTS_LIMIT,
            )}
            columns={columns}
            columnGroups={columnGroups}
            data={data?.results ?? []}
            isLoading={isLoading}
            actions={actions}
            state={{
              columnOrder: columnOrder,
              pageIndex: page,
              pageSize,
              sortBy: [sortBy],
              isAllRowsSelected: isAllSelected,
              selectedRowIds: selectedIds,
            }}
            getBulkActions={getBulkActions}
            onPaginate={onPaginate}
            onPageSizeChange={onPageSizeChange}
            onSetColumnOrder={onSetColumnOrder}
            onUpdate={handleUpdate}
            onLoadView={onLoadView}
            onSaveView={onSaveView}
            rowDetails={{
              onClick: showDetailsPanel,
              condition: (documentDetails: SearchDocumentItem) =>
                documentDetails?.document_handlers,
            }}
            options={{
              enableExportXlsx: false,
              enablePageSizeSelect: true,
              enableSelectRows: isBulkActionOnDashboardEnabled,
              enableStickyHeader: true,
              stickyHeaderOffset: NAV_AND_SUBNAV_HEIGHT,
            }}
            reactTableOptions={{
              autoResetSortBy: false,
              disableSortRemove: true,
              manualSortBy: true,
            }}
          />
          {renderMultipleLocationModal(null)}
        </>

        {isUnifiedDetailsPanelEnabled ? (
          <DocumentDetailsPanel
            docHandlerId={documentDetails?.document_handlers.map(
              (handler) => handler.id,
            )}
            searchKeyworkMatches={documentDetails?.full_text_matches}
            onHide={hideDetailsPanel}
            featuredClauses={featuredClauses}
            actionsConfig={{
              navigate: {
                onClick: onOpenDocumentClick,
              },
              download: {
                disabled:
                  documentDetails &&
                  !canDownloadSearchResultItem(documentDetails),
              },
              next: {
                onClick: next,
                disabled:
                  data && currentSelectedIndex === data.results.length - 1,
              },
              previous: {
                onClick: prev,
                disabled: currentSelectedIndex === 0,
              },
              delete: {
                onSuccess: refreshSearch,
              },
            }}
          />
        ) : (
          <DetailsPanel
            document={documentDetails}
            onHide={hideDetailsPanel}
            actions={getPanelActions(data?.results || [], documentDetails!)}
          />
        )}
        <ExportExcel
          query={query}
          isVisible={showExportExcel}
          onHide={() => setShowExportExcel(false)}
          totalDocs={data?.meta?.total}
          totalDupsAndDocs={data?.meta?.total_docs_including_dups}
          selectedFieldIds={selectedFieldIds}
        />
      </Layout>
    </ContentContainer>
  );
};

export default DashboardV2SearchResults;
